import ReactMarkdown from "react-markdown";
import { Link } from "@radix-ui/themes";
import { rewriteUrl } from "~/utils/rewrite-news-urls";

export const renderMarkdown = (content: string) => {
  return (
    <ReactMarkdown
      components={{
        a: (props) => {
          const href = rewriteUrl(props.href || "");

          return (
            <Link
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--demo-primary-color, #4095A8)",
                textDecoration: "underline",
              }}
            >
              {props.children}
            </Link>
          );
        },
        p: ({ children }) => <>{children}</>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

function extractExplanation(
  analysisResults: Record<string, unknown>,
  key: string
): string | null {
  if ("explanations" in analysisResults) {
    const explanations = analysisResults.explanations as Record<
      string,
      unknown
    >;
    if (typeof explanations[key] === "string")
      return explanations[key] as string;
    if (explanations[key] && typeof explanations[key] === "object") {
      const e = explanations[key] as Record<string, unknown>;
      const pos = Array.isArray(e.positive_explanation)
        ? e.positive_explanation.filter(Boolean).join("\n\n")
        : typeof e.positive_explanation === "string"
        ? e.positive_explanation
        : null;
      const neg = Array.isArray(e.negative_explanation)
        ? e.negative_explanation.filter(Boolean).join("\n\n")
        : typeof e.negative_explanation === "string"
        ? e.negative_explanation
        : null;
      return [pos, neg].filter(Boolean).join("\n\n") || null;
    }
  }
  const pos = analysisResults[`${key}_positive`] as string | undefined;
  const neg = analysisResults[`${key}_negative`] as string | undefined;
  return [pos, neg].filter(Boolean).join("\n\n") || null;
}

/**
 * Extract the textual content for a given portfolio tab from analysis results.
 * Used by the rewriter to send only the active tab's content.
 */
export function getActiveTabContent(
  analysisResults: Record<string, unknown>,
  tabKey: string
): string | null {
  // Talk tracks — combine all available narratives
  if (tabKey === "talk_tracks") {
    const parts: string[] = [];
    const highlights = analysisResults.portfolio_highlights as
      | string[]
      | undefined;
    if (highlights?.length) parts.push(highlights.filter(Boolean).join("\n\n"));
    return parts.length ? parts.join("\n\n") : null;
  }

  // Macro / Sector / Security tabs — structurally identical extraction
  if (tabKey === "macro" || tabKey === "sector" || tabKey === "security") {
    return extractExplanation(analysisResults, tabKey);
  }

  // Key developments tab
  if (tabKey === "key_developments") {
    if ("explanations" in analysisResults) {
      const explanations = analysisResults.explanations as Record<
        string,
        unknown
      >;
      const keyDev = explanations.key_developments as
        | Record<string, unknown>
        | undefined;
      if (typeof keyDev?.summary === "string") return keyDev.summary;
    }
    return null;
  }

  // Theme explanations tab
  if (tabKey === "theme_explanations") {
    if ("explanations" in analysisResults) {
      const explanations = analysisResults.explanations as Record<
        string,
        unknown
      >;
      const themes = explanations.theme_explanations as
        | Record<string, string>
        | undefined;
      if (themes) return Object.values(themes).filter(Boolean).join("\n\n");
    }
    return null;
  }

  // "all" tab or fallback — send everything
  return null;
}

// Extract "What's going well" and "What's not" sections from markdown
export const parseInsightSections = (markdownText: string) => {
  const sections = { positive: "", negative: "" };

  // Match "What's going well" with ellipsis or colon - use exec for capturing groups
  const positiveRegex = /what['']s going well[…:.]*\s*([\s\S]*?)(?=what['']s not|$)/gi;
  const positiveMatch = positiveRegex.exec(markdownText);
  if (positiveMatch && positiveMatch[1]) {
    sections.positive = positiveMatch[1].trim();
  }

  // Match "What's not" with ellipsis or colon - use exec for capturing groups
  const negativeRegex = /what['']s not[…:.]*\s*([\s\S]*?)(?=what['']s going well|$)/gi;
  const negativeMatch = negativeRegex.exec(markdownText);
  if (negativeMatch && negativeMatch[1]) {
    sections.negative = negativeMatch[1].trim();
  }

  return sections;
};
